import React, { CSSProperties, useCallback } from 'react';
import clsx from 'clsx';
import Style from './checkboxes.module.scss';
import { AppCheckboxInput } from 'uikit/inputs';
import { FieldsWithType } from 'src/helpers/typeUtils';
import { BuilderFieldErrorProps, BuilderFieldError } from '../../wrappers/error/builderFieldError.component';
import { BuilderFieldLabelProps, BuilderFieldLabel } from '../../wrappers/label/builderFieldLabel.component';

export type AppCheckboxGroupFieldProps<
  TOption extends Record<Key, string | number>,
  Key extends FieldsWithType<TOption, string | number>,
> = {
  style?: CSSProperties;
  labelProps: BuilderFieldLabelProps;
  errorProps?: BuilderFieldErrorProps;
  disabled?: boolean;
  options: TOption[];
  value?: TOption[Key][];
  valueField: Key;
  labelField: keyof TOption;
  onChange?: (value: TOption[Key][] | null) => void;
};

export const AppCheckboxGroupField = <
  TOption extends Record<Key, string | number>,
  Key extends FieldsWithType<TOption, string | number>,
>({
  errorProps,
  labelProps,
  disabled,
  options,
  style,
  value,
  valueField,
  labelField,
  onChange,
}: AppCheckboxGroupFieldProps<TOption, Key>) => {
  const onChangeHandler = useCallback(
    (option: TOption) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        onChange?.([...(value || []), option[valueField]]);
        return;
      }

      const newValue = value?.filter((x: any) => x !== option[valueField]);
      onChange?.(newValue?.length ? newValue : null);
    },
    [onChange, value, valueField],
  );

  return (
    <BuilderFieldError {...errorProps}>
      <BuilderFieldLabel className={clsx(Style.labelContainer)} disabled={disabled} {...labelProps}>
        <div className={clsx(Style.labelContainer)} style={style}>
          {options.map((option) => {
            return (
              <AppCheckboxInput
                key={option[valueField]}
                label={option[labelField].toString()}
                disabled={disabled}
                checked={value?.includes(option[valueField])}
                onChange={onChangeHandler(option)}
              />
            );
          })}
        </div>
      </BuilderFieldLabel>
    </BuilderFieldError>
  );
};
